import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getHotArticles, formatCount } from '@/lib/content';
import { useReaddyForm } from '@/hooks/useReaddyForm';
import type { Article } from '@/types/content';

const NEWSLETTER_ADDR = 'https://readdy.ai/api/form/dah9lt3oh653ivfvnp90';

export default function SiteFooter() {
  const hot = getHotArticles(4);
  const newsletter = useReaddyForm({ submitAddr: NEWSLETTER_ADDR, honeypotName: 'website_alt' });

  return (
    <footer className="w-full bg-secondary-900 text-secondary-50">
      <div className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3" aria-label="猎奇吃瓜首页">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-2xl text-background-50 dark:text-foreground-950">
                <i className="ri-emotion-laugh-line" aria-hidden="true"></i>
              </span>
              <span className="font-heading text-2xl">猎奇吃瓜</span>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-secondary-100">
              专注奇葩人物与迷惑行为，沙雕新闻、离谱故事、搞笑怪事天天更新。围观魔幻现实，收获离谱又上头的快乐。
            </p>
            <div className="mt-5 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-800 text-lg">
                <i className="ri-wechat-line" aria-hidden="true"></i>
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-800 text-lg">
                <i className="ri-weibo-line" aria-hidden="true"></i>
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-800 text-lg">
                <i className="ri-rss-line" aria-hidden="true"></i>
              </span>
            </div>
          </div>

          <nav aria-label="页脚栏目导航">
            <h2 className="font-heading text-lg text-secondary-50">热门栏目</h2>
            <ul className="mt-4 flex flex-col gap-2">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-sm text-secondary-100 transition-colors hover:text-accent-300"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h2 className="font-heading text-lg text-secondary-50">最热内容</h2>
            <ul className="mt-4 flex flex-col gap-3">
              {hot.map((item: Article) => (
                <li key={item.id}>
                  <Link
                    to={`/article/${item.id}`}
                    className="group flex flex-col gap-1 text-sm text-secondary-100 transition-colors hover:text-accent-300"
                  >
                    <span className="line-clamp-2">{item.title}</span>
                    <span className="text-xs text-secondary-300">
                      {formatCount(item.views)} 次围观
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-lg text-secondary-50">订阅今日瓜单</h2>
            <p className="mt-4 text-sm text-secondary-100">
              每天一封，只发最离谱的那几条，不发广告。
            </p>
            <form
              data-readdy-form
              className="mt-4 flex flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                newsletter.submit(e.currentTarget);
              }}
            >
              <label htmlFor="footer-news-email" className="sr-only">
                邮箱地址
              </label>
              <input
                id="footer-news-email"
                type="email"
                name="email"
                required
                placeholder="你的邮箱地址"
                autoComplete="email"
                className="w-full rounded-md border border-secondary-700 bg-secondary-800 px-3 py-2 text-sm text-secondary-50 outline-none placeholder:text-secondary-300 focus:border-accent-400"
              />
              <input
                className="gq-deco-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                disabled={newsletter.status === 'submitting'}
                className="flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                {newsletter.status === 'submitting' ? '提交中…' : '订阅瓜单'}
              </button>
            </form>
            {newsletter.status === 'success' && (
              <p className="mt-2 text-xs text-accent-300">订阅成功，明天见瓜！</p>
            )}
            {newsletter.status === 'error' && (
              <p className="mt-2 text-xs text-secondary-200">{newsletter.formError}</p>
            )}
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-secondary-700 pt-6 md:flex-row">
          <p className="text-xs text-secondary-200">
            © 2026 猎奇吃瓜 · 内容整理与分享，仅供娱乐阅读
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/about" className="text-xs text-secondary-200 hover:text-accent-300">
              关于我们
            </Link>
            <Link to="/category/shadiao-xinwen" className="text-xs text-secondary-200 hover:text-accent-300">
              沙雕新闻
            </Link>
            <Link to="/category/mohuan-xianshi" className="text-xs text-secondary-200 hover:text-accent-300">
              魔幻现实
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}